import { useState } from 'react';
import { RejectionRecord, ParsedIssue, CorrectionInput, applyCorrections } from '../../lib/api/intelligence';

interface RejectionAdvisoryPanelProps {
  rejection: RejectionRecord;
  onCorrectionsApplied?: (updated: RejectionRecord) => void;
}

const SEVERITY_STYLES: Record<ParsedIssue['severity'], string> = {
  rejection: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
  deficiency: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
  revision: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',
  warning: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
};

export default function RejectionAdvisoryPanel({ rejection, onCorrectionsApplied }: RejectionAdvisoryPanelProps) {
  // Track which issue indexes have been accepted (true) or rejected (false)
  const [decisions, setDecisions] = useState<Record<number, boolean>>({});
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [appliedRecord, setAppliedRecord] = useState<RejectionRecord | null>(null);

  const issues = rejection.parsed_issues;
  const acceptedCount = Object.values(decisions).filter(Boolean).length;

  const decide = (idx: number, accepted: boolean) => {
    setDecisions(prev => ({ ...prev, [idx]: accepted }));
  };

  const handleApply = async () => {
    const corrections: CorrectionInput[] = issues
      .map((issue, idx) => ({ issue, idx }))
      .filter(({ idx }) => decisions[idx] === true)
      .map(({ issue, idx }) => ({
        issue_index: idx,
        field_name: issue.field_name,
        applied_value: issue.expected_value,
      }));

    setApplying(true);
    setApplyError(null);
    try {
      const updated = await applyCorrections(rejection.id, corrections);
      setAppliedRecord(updated);
      onCorrectionsApplied?.(updated);
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'Failed to apply corrections');
    } finally {
      setApplying(false);
    }
  };

  // Already applied state
  const record = appliedRecord ?? rejection;
  const alreadyApplied = record.correction_status !== 'none' && record.accepted_corrections?.length > 0;
  const appliedFieldNames = new Set(record.accepted_corrections?.map(c => c.field_name) ?? []);

  // Pending parse state
  if (rejection.parse_status === 'pending') {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-8 text-center">
        <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Parsing rejection notes…</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">This usually takes a few seconds.</p>
      </div>
    );
  }

  // No issues parsed
  if (issues.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-8 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">No actionable issues were identified in the rejection notes.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-white/[0.05] px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Rejection Advisory
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {rejection.agency} · {rejection.form_type.toUpperCase()}
              {rejection.rejection_date && (
                <span className="ml-2">{new Date(rejection.rejection_date).toLocaleDateString()}</span>
              )}
            </p>
          </div>
          {alreadyApplied && (
            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300 capitalize">
              {record.correction_status === 'all_applied' ? 'All fixes applied' : 'Partially applied'}
            </span>
          )}
        </div>
      </div>

      {/* Issue cards */}
      <div className="p-6 space-y-4">
        {issues.map((issue, idx) => {
          const decision = decisions[idx];
          const wasApplied = alreadyApplied && appliedFieldNames.has(issue.field_name);
          return (
            <div
              key={idx}
              className={`rounded-xl border p-5 transition-colors ${
                wasApplied
                  ? 'border-green-200 bg-green-50 dark:border-green-500/20 dark:bg-green-500/5'
                  : decision === true
                  ? 'border-brand-200 bg-brand-50 dark:border-brand-500/20 dark:bg-brand-500/5'
                  : decision === false
                  ? 'border-gray-200 bg-gray-50 dark:border-white/[0.05] dark:bg-white/[0.01] opacity-60'
                  : 'border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]'
              }`}
            >
              {/* Issue header */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${SEVERITY_STYLES[issue.severity]}`}>
                    {issue.severity}
                  </span>
                  <span className="font-mono text-sm font-semibold text-gray-800 dark:text-white/90">
                    {issue.field_name}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {Math.round(issue.confidence * 100)}% confidence
                  </span>
                </div>
                {wasApplied && (
                  <span className="text-xs font-semibold text-green-600 dark:text-green-400 shrink-0">
                    ✓ Applied
                  </span>
                )}
              </div>

              {/* Advisory rows */}
              <div className="space-y-2 mb-4">
                <div className="grid grid-cols-[110px_1fr] gap-2 items-start">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 pt-0.5">Rejected because</span>
                  <span className="text-sm text-gray-800 dark:text-white/90">{issue.description}</span>
                </div>

                {issue.policy_references && issue.policy_references.length > 0 && (
                  <div className="grid grid-cols-[110px_1fr] gap-2 items-start">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 pt-0.5">Policy / standard</span>
                    <div className="flex flex-wrap gap-1">
                      {issue.policy_references.map((ref, i) => (
                        <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/[0.05] text-xs font-mono text-gray-700 dark:text-gray-300">
                          {ref}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {issue.expected_value && (
                  <div className="grid grid-cols-[110px_1fr] gap-2 items-start">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 pt-0.5">Suggested fix</span>
                    <span className="text-sm font-semibold text-green-700 dark:text-green-400 font-mono">{issue.expected_value}</span>
                  </div>
                )}
              </div>

              {/* Accept / Reject buttons — hidden once corrections are applied */}
              {!alreadyApplied && (
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => decide(idx, false)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      decision === false
                        ? 'bg-gray-100 border-gray-300 text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-white/[0.05] dark:text-gray-400 dark:hover:bg-white/[0.05]'
                    }`}
                  >
                    ✗ Reject
                  </button>
                  <button
                    onClick={() => decide(idx, true)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      decision === true
                        ? 'bg-brand-50 border-brand-400 text-brand-700 dark:bg-brand-500/10 dark:border-brand-400 dark:text-brand-300'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-white/[0.05] dark:text-gray-400 dark:hover:bg-white/[0.05]'
                    }`}
                  >
                    ✓ Accept
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {!alreadyApplied && (
        <div className="border-t border-gray-200 dark:border-white/[0.05] px-6 py-4 flex items-center justify-between gap-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {acceptedCount > 0
              ? `${acceptedCount} fix${acceptedCount !== 1 ? 'es' : ''} accepted`
              : 'Accept fixes above to apply them to this form'}
          </p>
          <div className="flex items-center gap-3">
            {applyError && (
              <span className="text-sm text-red-600 dark:text-red-400">{applyError}</span>
            )}
            <button
              onClick={handleApply}
              disabled={acceptedCount === 0 || applying}
              className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {applying
                ? 'Applying…'
                : acceptedCount > 0
                ? `Apply ${acceptedCount} accepted fix${acceptedCount !== 1 ? 'es' : ''}`
                : 'Apply accepted fixes'}
            </button>
          </div>
        </div>
      )}

      {appliedRecord && !applyError && (
        <div className="border-t border-green-200 dark:border-green-500/20 bg-green-50 dark:bg-green-500/5 px-6 py-4 rounded-b-2xl">
          <p className="text-sm text-green-700 dark:text-green-400 font-medium">
            ✓ {record.accepted_corrections.length} correction{record.accepted_corrections.length !== 1 ? 's' : ''} saved.
            Return to the Filing Tracker when ready to resubmit.
          </p>
        </div>
      )}
    </div>
  );
}
